// 导入axios库，用于发送HTTP请求
import axios from 'axios';
import { config } from '../config';
import userAuthService from './userAuthService';

// 后端API的基础URL
const API_BASE_URL = config.API_BASE_URL;

/**
 * 创建axios实例
 * 功能：配置默认的请求设置，包括基础URL和默认请求头
 */
const api = axios.create({
  baseURL: API_BASE_URL,  // 设置API基础URL
  timeout: config.REQUEST_TIMEOUT, // 请求超时时间
  headers: {
    'Content-Type': 'application/json', // 默认请求头为JSON格式
  },
});

// 简化的请求拦截器：C 端接口只注入 user token，不混用 admin token
api.interceptors.request.use(
  (cfg) => {
    // 如果是 FormData，移除 Content-Type，让浏览器自动设置 boundary
    if (cfg.data instanceof FormData) {
      if (cfg.headers) {
        delete cfg.headers['Content-Type'];
      }
    }
    // 只注入 user_access_token，admin token 走 adminApi 实例
    const userToken = localStorage.getItem('user_access_token');
    if (userToken) {
      cfg.headers = cfg.headers || {};
      cfg.headers['Authorization'] = `Bearer ${userToken}`;
    }
    return cfg;
  },
  (error) => {
    console.error('请求错误:', error);
    return Promise.reject(error);
  }
);

// 简化的响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    // access token 过期时，尝试用 refresh token 换新 token，只重试一次
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      localStorage.getItem('user_refresh_token')
    ) {
      originalRequest._retry = true;
      try {
        const newToken = await userAuthService.refresh();
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (_) {
        // refresh 也失败，清除登录态，跳转登录页
        userAuthService.clear();
        sessionStorage.setItem('login_redirect', window.location.pathname);
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }
    // 未登录用户访问受保护接口，跳转登录页并记录来源路径
    if (error.response?.status === 401 && !originalRequest._retry) {
      sessionStorage.setItem('login_redirect', window.location.pathname);
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (error.response) {
      console.error('API错误:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('网络错误:', error.message);
    } else {
      console.error('请求错误:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * 直播间相关API接口集合
 * 功能：封装所有与直播间相关的API调用
 */
// 简化的重试机制
const retryRequest = async (fn, retries = 2) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return retryRequest(fn, retries - 1);
    }
    throw error;
  }
};

export const streamAPI = {
  /**
   * 获取所有直播间列表
   * @param {boolean} includeInactive - 是否包含非活跃直播间，默认true
   * @returns {Promise} - 返回包含所有直播间的Promise对象
   * 功能：从后端获取所有直播间的信息
   */
  getStreams: (includeInactive = true, publicOnly = false) => retryRequest(() => api.get('/streams', {
    params: {
      include_inactive: includeInactive,
      public_only: publicOnly,
    }
  })),
  
  /**
   * 获取特定直播间的详细信息
   * @param {string|number} id - 直播间ID
   * @returns {Promise} - 返回特定直播间信息的Promise对象
   * 功能：根据ID获取单个直播间的详细信息
   */
  getStream: (id, { publicOnly = false } = {}) => retryRequest(() => api.get(`/streams/${id}`, {
    params: { public_only: publicOnly }
  })),
  
  /**
   * 获取账号的创建状态
   * @param {string|number} accountId - 账号ID
   * @param {string} userSessionId - 用户会话ID（可选）
   * @returns {Promise} - 返回账号创建状态的Promise对象
   * 功能：检查账号是否有正在创建的直播间
   */
  getAccountCreationStatus: (accountId, userSessionId = null) => {
    const params = {};
    if (userSessionId) {
      params.user_session_id = userSessionId;
    }
    return retryRequest(() => api.get(`/accounts/${accountId}/creation-status`, { params }));
  },

  // 新接口：按 user_id 查询创建状态（替代 getAccountCreationStatus）
  getUserCreationStatus: (userId, userSessionId = null) => {
    const params = {};
    if (userSessionId) {
      params.user_session_id = userSessionId;
    }
    return retryRequest(() => api.get(`/users/${userId}/creation-status`, { params }));
  },
  
  /**
   * 创建新直播间
   * @param {FormData|Object} data - 直播间数据（可以是FormData或普通对象）
   * @returns {Promise} - 返回创建结果的Promise对象
   * 功能：
   * 1. 处理文件上传（角色图片、场景图片）
   * 2. 自动设置正确的Content-Type
   * 3. 提交直播间创建请求
   */
  createStream: (data) => {
    const reqConfig = {};
    if (data instanceof FormData) {
      reqConfig.headers = { 'Content-Type': 'multipart/form-data' };
    }
    return api.post('/create-stream', data, reqConfig);
  },
  
  /**
   * 获取直播间的视频列表
   * @param {string|number} streamId - 直播间ID
   * @returns {Promise} - 返回视频列表的Promise对象
   * 功能：获取指定直播间的所有正式视频
   */
  getStreamVideos: (streamId, { publicOnly = false } = {}) => api.get(`/streams/${streamId}/videos`, {
    params: { public_only: publicOnly }
  }),
  
  /**
   * 获取直播间的候选视频列表
   * @param {string|number} streamId - 直播间ID
   * @returns {Promise} - 返回候选视频列表的Promise对象
   * 功能：获取指定直播间的所有候选视频（等待用户选择）
   */
  getCandidateVideos: (streamId) => retryRequest(() => api.get(`/streams/${streamId}/candidate-videos`)),
  
  /**
   * 提交选中的视频
   * @param {string|number} streamId - 直播间ID
   * @param {Array<number>} videoIds - 选中的视频ID数组
   * @returns {Promise} - 返回提交结果的Promise对象
   * 功能：将候选视频转为正式视频，删除未选中的视频
   */
  selectVideos: (streamId, videoIds) => {
    return api.post(`/streams/${streamId}/select-videos`, { video_ids: videoIds });
  },
  
  /**
   * 为直播间创建新视频
   * @param {string|number} streamId - 直播间ID
   * @param {Object} data - 视频数据（包含互动类型等）
   * @returns {Promise} - 返回创建结果的Promise对象
   * 功能：根据用户互动生成新的视频内容
   */
  createVideo: (streamId, data) => api.post(`/streams/${streamId}/videos`, data),
  
  /**
   * 获取直播间的创作进程信息
   * @param {string|number} streamId - 直播间ID
   * @returns {Promise} - 返回创作进程信息的Promise对象
   * 功能：获取创作过程中的媒体文件和进度信息
   */
  getStreamCreationProgress: (streamId) => retryRequest(() => api.get(`/streams/${streamId}/creation-progress`)),
  
  /**
   * 取消直播间创建
   * @param {string|number} streamId - 直播间ID
   * @returns {Promise} - 返回取消结果的Promise对象
   * 功能：将卡住的直播间创建状态设为 failed
   */
  cancelStreamCreation: (streamId) => api.post(`/streams/${streamId}/cancel-creation`),
  
  // 移除了getVideoUrl方法，直接使用配置中的URL构建
};

/**
 * 背景音乐相关API接口集合
 * 功能：封装所有与背景音乐相关的API调用
 */
export const musicAPI = {
  /**
   * 获取所有背景音乐列表
   * @returns {Promise} - 返回背景音乐列表的Promise对象
   */
  getBackgroundMusic: () => retryRequest(() => api.get('/background-music')),
  
  /**
   * 上传背景音乐文件
   * @param {FormData} formData - 包含音乐文件和元数据的FormData
   * @returns {Promise} - 返回上传结果的Promise对象
   */
  uploadBackgroundMusic: (formData) => {
    return api.post('/background-music', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  /**
   * 获取背景音乐URL
   * @param {number} musicId - 音乐ID
   * @returns {Promise} - 返回音乐URL的Promise对象
   */
  getMusicUrl: (musicId) => retryRequest(() => api.get(`/background-music/${musicId}/url`)),
  
  /**
   * 删除背景音乐
   * @param {number} musicId - 音乐ID
   * @returns {Promise} - 返回删除结果的Promise对象
   */
  deleteBackgroundMusic: (musicId) => retryRequest(() => api.delete(`/background-music/${musicId}`)),
};

// 移除了accountAPI和followAPI，因为实际使用中不需要这些功能

// 导出axios实例，供其他模块使用
export default api;

